import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  SystemUpdate,
  CloudDownload,
  Schedule,
  Storage,
  NewReleases,
} from '@mui/icons-material';
import { Firmware } from '../types';
import { firmwareApi, taskApi, deviceApi } from '../api';
import { productLabel } from '../flash/profiles';
import { formatBytes, formatDateTime, useI18n } from '../i18n';

interface FirmwareUpdateProps {
  deviceId: string;
}

const FirmwareUpdate: React.FC<FirmwareUpdateProps> = ({ deviceId }) => {
  const { t } = useI18n();
  const [firmware, setFirmware] = useState<Firmware[]>([]);
  const [selectedFirmware, setSelectedFirmware] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);

  const loadFirmware = async () => {
    try {
      setError(null);
      let deviceModel = '';
      try {
        const device = await deviceApi.getDevice(deviceId);
        deviceModel = device.model || '';
      } catch {
        deviceModel = '';
      }
      const firmwareList = await firmwareApi.getFirmware(undefined, deviceModel);
      setFirmware(firmwareList);
      setSelectedFirmware('');
    } catch (err) {
      setError(t('firmware.loadFailed'));
      console.error('Error loading firmware:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFirmware();
  }, [deviceId]);

  const handleUpdateFirmware = async () => {
    if (!selectedFirmware) return;

    try {
      setUpdating(true);
      setError(null);

      // Create update task with firmware filename as parameter
      await taskApi.createTask(deviceId, {
        command: `update ${selectedFirmware}`,
        data: { 
          firmware: selectedFirmware,
          version: selectedFirmware.replace('vcs-iot-controller_', '').replace('.bin', '')
        }
      });

      setConfirmDialog(false);
      setSelectedFirmware('');
      
      // Show success message
      alert(t('firmware.created'));
      
    } catch (err) {
      setError(t('firmware.createFailed'));
      console.error('Error creating update task:', err);
    } finally {
      setUpdating(false);
    }
  };

  const selectedFirmwareInfo = firmware.find(f => f.filename === selectedFirmware);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" p={3}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {t('firmware.loading')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SystemUpdate color="primary" />
            {t('firmware.title')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {firmware.length === 0 ? (
            <Box textAlign="center" py={3}>
              <Typography variant="body2" color="text.secondary">
                {t('firmware.empty')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('firmware.emptyHint')}
              </Typography>
            </Box>
          ) : (
            <Box>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t('firmware.selectVersion')}</InputLabel>
                <Select
                  value={selectedFirmware}
                  onChange={(e) => setSelectedFirmware(e.target.value)}
                  label={t('firmware.selectVersion')}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>{t('firmware.selectVersion')}</em>
                  </MenuItem>
                  {firmware.map((fw) => (
                    <MenuItem key={fw.filename} value={fw.filename}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Box>
                          <Typography variant="body2">
                            {fw.environment ? `${productLabel(fw.environment, t)} — ` : ''}
                            {fw.versionString || fw.filename}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatBytes(fw.size)} • {formatDateTime(fw.buildTime || fw.created)}
                          </Typography>
                        </Box>
                        {fw.isLatest && (
                          <Chip 
                            label={t('common.latest')} 
                            size="small" 
                            color="primary" 
                            icon={<NewReleases />}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedFirmwareInfo && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('firmware.selectedDetails')}
                  </Typography>
                  {selectedFirmwareInfo.versionString && (
                    <Typography variant="body2" gutterBottom>
                      <strong>{t('common.version')}:</strong> {selectedFirmwareInfo.versionString}
                    </Typography>
                  )}
                  <Box display="flex" gap={3} flexWrap="wrap">
                    {selectedFirmwareInfo.commit && (
                      <Typography variant="body2">
                        <strong>{t('common.commit')}:</strong> {selectedFirmwareInfo.commit}
                        {selectedFirmwareInfo.branch ? ` (${selectedFirmwareInfo.branch})` : ''}
                      </Typography>
                    )}
                    <Box display="flex" alignItems="center" gap={1}>
                      <Storage fontSize="small" />
                      <Typography variant="body2">
                        {formatBytes(selectedFirmwareInfo.size)}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Schedule fontSize="small" />
                      <Typography variant="body2">
                        {formatDateTime(selectedFirmwareInfo.buildTime || selectedFirmwareInfo.created)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              <Button
                variant="contained"
                color="warning"
                startIcon={updating ? <CircularProgress size={16} /> : <CloudDownload />}
                onClick={() => setConfirmDialog(true)}
                disabled={!selectedFirmware || updating}
                fullWidth
              >
                {updating ? t('firmware.creating') : t('firmware.start')}
              </Button>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t('firmware.restartHint')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>
          {t('firmware.confirmTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {t('firmware.confirmBody')}
          </Typography>
          
          {selectedFirmwareInfo && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('firmware.updateDetails')}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary={t('firmware.file')}
                    secondary={selectedFirmwareInfo.filename}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={t('firmware.fileSize')}
                    secondary={formatBytes(selectedFirmwareInfo.size)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={t('firmware.buildDate')}
                    secondary={formatDateTime(selectedFirmwareInfo.created)}
                  />
                </ListItem>
              </List>
            </Box>
          )}

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>{t('firmware.warningTitle')}:</strong> {t('firmware.warningBody')}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleUpdateFirmware} 
            variant="contained" 
            color="warning"
            disabled={updating}
          >
            {updating ? t('firmware.creatingShort') : t('firmware.startUpdate')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FirmwareUpdate;
